import { Box, Button, Divider, Paper, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import FileUpload from 'components/FileUpload'
import { excelExt } from 'config/extensions'
import { axiosPost } from 'connectors/axiosPost'
import { useAlert } from 'context/AlertContext'
import { useAuthContext } from 'context/AuthContext'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type FormFields = {
  file: File | null
}

export type UpdatePrizesFormProps = {
  prizeGroupId: string
}

export const UpdatePrizesForm = ({ prizeGroupId }: UpdatePrizesFormProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'prize' })
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()
  const queryClient = useQueryClient()

  const { mutate, isLoading } = useMutation(
    async (body: FormData) =>
      await axiosPost<{ affected: number }>(token, refreshToken, `prizes/update/values/${prizeGroupId}`, body),
    {
      onSuccess: response => {
        if (response !== undefined && !('error' in response)) {
          void queryClient.refetchQueries(['prize-group-data', `prize-${prizeGroupId}`])
          changeMessage('Prizes updated', 'success')
        }
      },
    }
  )

  const { handleSubmit, control } = useForm<FormFields>({ defaultValues: { file: null } })

  const onSubmit = (data: FormFields) => {
    const fd = new FormData()

    if (data.file && !('id' in data.file)) {
      fd.append('file', data.file)

      mutate(fd)
    }
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h6" fontWeight="bold" component="h2">
        {t('updateFile')}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FileUpload extensions={[...excelExt]} name="file" type="file" label={t('attachment')} control={control} />
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('save')}
          </Button>
        </Box>
      </form>
    </Paper>
  )
}
